import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPollByAny,
  selectCurrentPoll,
  selectPollsLoading,
} from "@/store/redux/slices/polls_&_survey/polls_&_survey";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import Breadcrumb from "@/components/specific/Breadcrumb";
import HeaderImage from "@/pages/polls_&_survey/components/single_polls_&_survey/Header_Image";
import PollInfo from "@/pages/polls_&_survey/components/single_polls_&_survey/Polls_Info";
import PollResults from "@/pages/polls_&_survey/components/single_polls_&_survey/Polls_Result";
import CreatorCard from "@/pages/polls_&_survey/components/single_polls_&_survey/Creator_Card";
import ActionButtons from "@/pages/polls_&_survey/components/single_polls_&_survey/Action_Button";
import Success from "@/components/common/alerts/Success";
import { deletePollSurvey } from "@/store/redux/slices/polls_&_survey/polls_&_survey";

const SinglePollsAndSurvey = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentPoll = useSelector(selectCurrentPoll);
  const isLoading = useSelector(selectPollsLoading);
  const { showLoader, hideLoader } = useLoader();
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  useEffect(() => {
    const fetchPoll = async () => {
      if (id) {
        showLoader();
        try {
          await dispatch(fetchPollByAny({ key: "_id", value: id })).unwrap();
          // Success handling if needed
        } catch (error) {
          console.error("Error fetching poll:", error);
          // Error handling if needed
        } finally {
          hideLoader();
        }
      }
    };

    fetchPoll();
  }, [dispatch, hideLoader, id, showLoader]);

  const handleDeleteSuccess = () => {
    setShowDeleteSuccess(true);
  };

  const handleSuccessClose = () => {
    setShowDeleteSuccess(false);
    navigate("/dashboard/polls");
  };

  // Replace with a dedicated close endpoint when the backend provides one.
  const handleClosePollEarly = async () => {
    showLoader();
    try {
      await dispatch(deletePollSurvey(id)).unwrap();
      hideLoader();
      handleDeleteSuccess();
    } catch (error) {
      console.error("Failed to close poll early:", error);
      hideLoader();
    }
  };

  // Build a shareable public link from the poll's own link field or the current URL.
  const publicLink =
    currentPoll?.link ||
    currentPoll?.shareLink ||
    `${window.location.origin}/#/dynamic/polls/${id}`;

  if (isLoading) {
    return null;
  }

  if (!currentPoll) {
    return <div>Poll not found</div>;
  }

  // Format the date from API
  const formattedDate = new Date(currentPoll.schedule.startDate)
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();

  // breadcrumb items
  const items = [
    { title: "Polls & Survey", link: "/dashboard/polls" },
    { title: currentPoll?.name || "Poll/Survey Details", link: null },
  ];

  return (
    <div className="py-6">
      <div className="mb-4">
        <Breadcrumb items={items} />
        <Back text="Back" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-10">
        <div className="col-span-2">
          <HeaderImage candidates={currentPoll?.media} />

          <div className="mb-6">
            <PollInfo
              date={formattedDate}
              time={currentPoll.schedule.startTime}
              title={currentPoll.name}
              description={currentPoll.description}
              duration={`${currentPoll.schedule.startDate} to ${currentPoll.schedule.endDate}`}
              totalRespondents={currentPoll.respondentCount ?? 0}
              respondents={currentPoll.respondents ?? []}
            />
          </div>

          <div className="mb-6 rounded-lg bg-lightest">
            <PollResults
              results={currentPoll.results ?? []}
              respondents={currentPoll.respondents ?? []}
            />
          </div>
        </div>
        <div>
          <CreatorCard
            publicLink={publicLink}
            onClosePoll={handleClosePollEarly}
          />
          <div className="lg:hidden mt-4">
            <ActionButtons
              poll={currentPoll}
              onDeleteSuccess={handleDeleteSuccess}
            />
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Success
        isVisible={showDeleteSuccess}
        onClose={handleSuccessClose}
        header="Poll/Survey Deleted"
        paragraph="The poll/survey has been successfully deleted."
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

export default SinglePollsAndSurvey;
